import { Link, useNavigate } from 'react-router';
import { useState } from 'react';
import useTracking from './useTracking';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Sheet,
  Link as MuiLink,
  Chip,
  Alert,
  Stack,
  // Avatar,
  // AvatarGroup,
  Grid,
  Tooltip,
  LinearProgress,
} from '@mui/joy';
import useUser from './auth/useUser';
import { useMemo } from 'react';
import { getCdnFileUrl } from '../utils';
import LargeLoader from './LargeLoader';
import { ContentCopy, OpenInNew, Warning, Description } from '@mui/icons-material';
import { useFeedback } from './feedback/Service';
import useTeamPermissions from '../hooks/useTeamPermissions';
import ATSConnectBanner from './integrations/ATSConnectBanner';
import NonATSOnboardingBanner from './integrations/NonATSOnboardingBanner';

const Jobs = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { loading, error, user } = useUser({ pollInterval: 5 * 60 * 1000, refetchOnWindowVisible: true });
  const { trackPostNewJobButton, trackImportJobButton, trackCopyJobUrl, trackOpenDemo, trackViewCandidates } = useTracking();
  const permissions = useTeamPermissions(user?.client);
  console.log({ permissions });

  const [importBannerShowing, setImportBannerShowing] = useState(false);
  const data = useMemo(() => user?.client || {}, [user]);

  // Function to handle navigation to add new job page
  const handleAddNewJob = () => {
    trackPostNewJobButton(user?.client?._id);
    navigate('/onboarding/job/new');
  };

  const handleImportFromATS = async () => {
    await trackImportJobButton(data._id);
    navigate('/onboarding/import-job');
  };

  // Enhanced date formatting with relative time
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get job performance metrics
  const getJobMetrics = (job) => {
    const { total, finished, maxScore, averageScore, conversionRate } = job.candidatesStats;
    if (!job.candidatesStats || !total) {
      return {
        totalCandidates: 0,
        completedInterviews: 0,
        averageScore: 0,
        maxScore: 0,
        conversionRate: 0,
      };
    }
    return {
      totalCandidates: total,
      completedInterviews: finished,
      averageScore,
      maxScore,
      conversionRate,
    };
  };

  if (loading) return <LargeLoader />;

  if (error) return (
    <Box sx={{ p: 4 }}>
      <Typography level="h4" color="danger">
        Error loading jobs
      </Typography>
      <Typography color="danger">{error.message}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 0 } }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 2 },
          mb: 3,
        }}>
          <Typography level="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            {user.client.usesATS ? 'Imported ATS Jobs' : 'Jobs'}
          </Typography>
          {permissions.canEditJobs && (
            <Stack direction="row" spacing={1} alignItems="center">
              {data.selectedATSProvider && data.usesATS !== false && !importBannerShowing && (
                <Button
                  startDecorator={<WorkIcon />}
                  onClick={handleImportFromATS}
                  color="neutral"
                  variant="outlined"
                  size="sm"
                >
                  Import from ATS
                </Button>
              )}
              <Button
                startDecorator={<AddIcon />}
                onClick={handleAddNewJob}
                color="primary"
                variant="solid"
                size="sm"
                sx={{ boxShadow: 'sm', '&:hover': { boxShadow: 'md' } }}
              >
                Create New Job
              </Button>
            </Stack>
          )}
        </Box>

        {(permissions.canViewBilling && data.credits < 1) && (
          <Alert
            sx={{ alignItems: "center", gap: 2, mb: 3 }}
            startDecorator={<Warning />}
            variant="soft"
            color="danger"
            endDecorator={
              <Button
                size="sm"
                variant="solid"
                color="primary"
                component={Link}
                to="/settings/billing"
              >
                Buy Credits
              </Button>
            }
          >
            <div>
              <Typography level="title-md">
                Interviewing Paused
              </Typography>
              <Typography level="body-sm">
                You have 0 credits remaining. Candidates cannot complete interviews until you purchase more credits.
              </Typography>
            </div>
          </Alert>
        )}
      </Box>

      {data.selectedATSProvider && data.usesATS !== false && (
        <ATSConnectBanner
          providerId={data.selectedATSProvider}
          clientId={data._id}
          onImportStepVisible={setImportBannerShowing}
        />
      )}

      {permissions.canEditJobs && data.usesATS === false && (data.jobs.filter(job => !job.title.startsWith('[DEMO JOB]')) || []).length === 0 && (
        <NonATSOnboardingBanner />
      )}

      {/* Jobs List */}
      {(data.jobs || []).length === 0 ? (
        <Sheet
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 'lg',
            bgcolor: 'background.level1',
            border: '2px dashed',
            borderColor: 'divider'
          }}
        >
          <Typography level="h4" sx={{ mb: 2, color: 'text.tertiary' }}>
            🚀 Ready to hire amazing talent?
          </Typography>
          <Typography level="body-lg" sx={{ mb: 3 }}>
            Create your first job posting and start receiving applications from qualified candidates.
          </Typography>
          {permissions.canEditJobs &&
            <Button
              size="lg"
              startDecorator={<AddIcon />}
              onClick={handleAddNewJob}
              color="primary"
            >
              Post Your First Job
            </Button>
          }
        </Sheet>
      ) : (
        <Stack spacing={3}>
          {[...data.jobs].reverse().map((job, index) => {
            const metrics = getJobMetrics(job);
            // const recentApplications = (job.applications || [])
            //   .slice(0, 3)
            //   .map(app => ({
            //     name: `${app.user?.firstName || ''} ${app.user?.lastName || ''}`.trim(),
            //     avatar: app.user?.avatarUrl
            //   }));

            return (
              <Card
                key={job._id}
                id={index === 0 ? 'onboarding-first-job' : undefined}
                variant="outlined"
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 'lg',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Job Header */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <MuiLink
                          level="h4"
                          component={Link}
                          to={`/job/${job._id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            '&:hover': {
                              color: 'primary.500'
                            }
                          }}
                          underline='none'
                        >
                          {job.title}
                        </MuiLink>
                        <Chip
                          size="sm"
                          variant={job.published ? "solid" : "outlined"}
                          color={job.published ? "success" : "neutral"}
                          startDecorator={job.published ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        >
                          {job.published ? "Live" : "Draft"}
                        </Chip>
                      </Stack>

                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarTodayIcon fontSize="small" />
                          <Typography level="body-sm" color="neutral">
                            {formatDate(job.createdAt)}
                          </Typography>
                        </Stack>
                        {job.pdfUrl && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Description fontSize="small" />
                            <Typography level="body-sm" color="neutral">
                              Job Description
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      {job.comment && (
                        <Typography
                          level="body-sm"
                          sx={{
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            color: 'text.tertiary',
                          }}
                        >
                          {job.comment}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Metrics Section */}
                  {metrics.completedInterviews > 0 &&
                    <Grid container spacing={3} sx={{ mb: 2 }}>
                      <Grid xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography level="h3" color="primary">
                            {metrics.totalCandidates}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            Candidates
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography level="h3" color="success">
                            {metrics.completedInterviews}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            Interviews
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography level="h3" color={metrics.averageScore > 70 ? "success" : metrics.averageScore > 50 ? "warning" : "danger"}>
                            {metrics.averageScore}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            Avg Score
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography level="h3" color="neutral">
                            {metrics.conversionRate}%
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            Conversion
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  }

                  {/* Recent Candidates */}
                  {/* {recentApplications.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography level="body-sm" color="neutral">
                          Recent candidates:
                        </Typography>
                        <AvatarGroup size="sm" sx={{ '--AvatarGroup-gap': '-8px' }}>
                          {recentApplications.map((candidate, index) => (
                            <Tooltip key={index} title={candidate.name} placement="top">
                              <Avatar
                                size="sm"
                                src={candidate.avatar ? getCdnFileUrl(candidate.avatar) : undefined}
                              >
                                {candidate.name ? candidate.name.split(' ').map(n => n[0]).join('') : '?'}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {metrics.totalCandidates > 3 && (
                            <Avatar size="sm" color="neutral">
                              +{metrics.totalCandidates - 3}
                            </Avatar>
                          )}
                        </AvatarGroup>
                      </Stack>
                    </Box>
                  )} */}

                  {/* Performance Bar */}
                  {metrics.completedInterviews > 0 && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography level="body-xs" color="neutral">
                          Top Candidate Score
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {metrics.maxScore}/100
                        </Typography>
                      </Stack>
                      <LinearProgress
                        determinate
                        value={metrics.maxScore}
                        color={metrics.maxScore > 80 ? "success" : metrics.maxScore > 60 ? "warning" : "danger"}
                        size="sm"
                      />
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Stack
                    spacing={{ xs: 1, sm: 2 }}
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'column' }}
                  >
                    {/* First Row - Primary Actions */}
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                      }}
                    >
                      <Button
                        variant="outlined"
                        component={Link}
                        to={`/job/${job._id}`}
                        startDecorator={<EditIcon />}
                        color="primary"
                        size="sm"
                        sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' }, minWidth: { xs: 'auto', sm: 'auto' } }}
                      >
                        Edit Job
                      </Button>

                      <Button
                        variant="solid"
                        component={Link}
                        to={`/job/${job._id}/candidates`}
                        startDecorator={<GroupIcon />}
                        color="primary"
                        disabled={metrics.totalCandidates === 0}
                        size="sm"
                        id={index === 0 ? 'onboarding-view-candidates' : undefined}
                        sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' }, minWidth: { xs: 'auto', sm: 'auto' } }}
                        onClick={() => trackViewCandidates(user.client._id, job._id, job.title, metrics.totalCandidates)}
                      >
                        View Candidates ({metrics.completedInterviews})
                      </Button>
                    </Stack>

                    {/* Second Row - Secondary Actions */}
                    {(job.published || job.pdfUrl) && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          flexWrap: 'wrap',
                          gap: 1,
                          justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                        }}
                      >
                        {job.published &&
                          <Button
                            variant="soft"
                            endDecorator={<ContentCopy />}
                            color="neutral"
                            size="sm"
                            sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' }, minWidth: { xs: 'auto', sm: 'auto' } }}
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/company/${user.client._id}/job/${job._id}/intro`);
                              trackCopyJobUrl(user.client._id, job._id, job.title);
                              feedback.snackbar({ text: "Job URL copied to clipboard!", type: "success" });
                            }}
                          >
                            Copy Job URL
                          </Button>
                        }
                        <Button
                          variant="soft"
                          component="a"
                          href={`${window.location.origin}/company/${user.client._id}/job/${job._id}/intro`}
                          target="_blank"
                          rel="noopener noreferrer"
                          endDecorator={<OpenInNew />}
                          color="neutral"
                          size="sm"
                          id={index === 0 ? 'onboarding-open-demo' : undefined}
                          sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' }, minWidth: { xs: 'auto', sm: 'auto' } }}
                          onClick={() => trackOpenDemo(user.client._id, job._id)}
                        >
                          Open Demo
                        </Button>
                        {job.pdfUrl && (
                          <Button
                            variant="soft"
                            component="a"
                            href={getCdnFileUrl(job.pdfUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            endDecorator={<OpenInNew />}
                            color="neutral"
                            size="sm"
                            sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' }, minWidth: { xs: 'auto', sm: 'auto' } }}
                          >
                            View Description
                          </Button>
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        <Tooltip title="View Analytics" placement="top">
                          <Button
                            variant="plain"
                            color="neutral"
                            size="sm"
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              flex: { xs: '0 0 auto', sm: '0 0 auto' }
                            }}
                            component={Link}
                            to={`/dashboard?jobId=${job._id}`}
                          >
                            <AssessmentIcon />
                          </Button>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default Jobs;
